import React, { useState, useEffect } from 'react';
import { devLog } from '../utils/devTools';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  IconButton,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  FormLabel,
  Stack
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useFormContext } from '../context/FormContext';
import { StepProps, Attorney, TranscriptOrder, AdditionalServices } from '../types';
import FileUploadField from './FileUploadField';

const AttorneyInfo: React.FC<StepProps> = ({ onNext }) => {
  const { 
    formData, 
    updateNestedFormData, 
    addItemToArray, 
    removeItemFromArray,
    validateStep
  } = useFormContext();
  
  // Track expanded attorneys
  const [expandedAttorney, setExpandedAttorney] = useState<string>(
    formData.attorneys.length > 0 ? formData.attorneys[0].id : ''
  );
  
  // Track expanded deponent transcript orders
  const [expandedOrders, setExpandedOrders] = useState<{[key: string]: string}>({});
  
  // Initialize expanded transcript orders when component mounts
  useEffect(() => {
    // If there are attorneys and the first one is expanded, also expand its first transcript order
    if (formData.attorneys.length > 0 && expandedAttorney === formData.attorneys[0].id) {
      const firstAttorney = formData.attorneys[0];
      if (firstAttorney.transcriptOrders && firstAttorney.transcriptOrders.length > 0) {
        setExpandedOrders({
          [firstAttorney.id]: firstAttorney.transcriptOrders[0].deponentId
        });
      }
    }
  // ESLINT NOTE: Intentional mount-only effect — initializes expanded transcript order state on load.
  // Adding expandedAttorney or formData.attorneys would cause this to re-run on every accordion
  // open/close, resetting expandedOrders back to the first transcript order each time. 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // New attorney state - now including transcript orders for all deponents
  const [newAttorney, setNewAttorney] = useState<Attorney>(() => {
    // Create initial transcript orders for all existing deponents
    const initialTranscriptOrders = formData.deponents.map(deponent => ({
      deponentId: deponent.id,
      orderType: 'No Order' as const,
      additionalServices: {
        realtime: false,
        roughDraft: false,
        depositionSummary: false
      }
    }));
    
    // Generate a consistent ID that will be used throughout the process
    // This ID is created once when the component mounts and used for file uploads
    const initialId = `attorney-${Date.now()}-${formData.attorneys.length}`;
    devLog(`Initializing new attorney with ID: ${initialId}`);
    
    return {
      id: initialId,
      name: '',
      email: '',
      firm: '',
      mailingAddress: '',
      telephonic: false,
      transcriptOrders: initialTranscriptOrders
    };
  });

  // Handle accordion expansion for attorneys
  const handleAttorneyAccordion = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    const attorney = formData.attorneys.find(a => a.id === panel);
    
    // Set the expanded attorney
    setExpandedAttorney(isExpanded ? panel : '');
    
    // When expanding an attorney, also expand their first transcript order (if they have any)
    if (isExpanded && attorney && attorney.transcriptOrders.length > 0) {
      setExpandedOrders({
        ...expandedOrders,
        [panel]: attorney.transcriptOrders[0].deponentId
      });
    }
  };

  // Handle accordion expansion for transcript orders
  const handleOrderAccordion = (attorneyId: string, deponentId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedOrders({
      ...expandedOrders,
      [attorneyId]: isExpanded ? deponentId : ''
    });
  };

  // Basic attorney field change
  const handleChange = (index: number, field: keyof Attorney, value: Attorney[keyof Attorney]) => {
    updateNestedFormData('attorneys', index, field, value);
  };

  // Additional services change for transcript orders
  const handleServiceChange = (
    attorneyIndex: number, 
    orderIndex: number, 
    service: keyof AdditionalServices, 
    value: boolean
  ) => {
    const attorney = formData.attorneys[attorneyIndex];
    const order = attorney.transcriptOrders[orderIndex];
    
    const updatedServices = {
      ...(order.additionalServices || {
        realtime: false,
        roughDraft: false,
        depositionSummary: false
      }),
      [service]: value
    };
    
    const updatedOrder = {
      ...order,
      additionalServices: updatedServices
    };
    
    const updatedOrders = [...attorney.transcriptOrders];
    updatedOrders[orderIndex] = updatedOrder;
    
    updateNestedFormData('attorneys', attorneyIndex, 'transcriptOrders', updatedOrders);
  };

  // Handle transcript order change
  const handleTranscriptOrderChange = (
    attorneyIndex: number,
    orderIndex: number,
    field: keyof TranscriptOrder,
    value: TranscriptOrder[keyof TranscriptOrder]
  ) => {
    const attorney = formData.attorneys[attorneyIndex];
    const updatedOrders = [...attorney.transcriptOrders];
    updatedOrders[orderIndex] = {
      ...updatedOrders[orderIndex],
      [field]: value
    };
    
    updateNestedFormData('attorneys', attorneyIndex, 'transcriptOrders', updatedOrders);
  };

  // We've removed the addTranscriptOrder function since we're auto-populating all deponents

  // Since we want all deponents to have transcript orders for each attorney,
  // we'll change this to reset an order to "No Order" type instead of removing it entirely
  const resetTranscriptOrder = (attorneyIndex: number, orderIndex: number) => {
    const attorney = formData.attorneys[attorneyIndex];
    const updatedOrders = [...attorney.transcriptOrders];
    updatedOrders[orderIndex] = {
      ...updatedOrders[orderIndex],
      orderType: 'No Order',
      transcriptFormat: undefined,
      specialNote: undefined,
      deliveryType: undefined,
      deliveryTimeline: undefined,
      dueDate: undefined,
      orderSubmissionMethod: undefined,
      orderSubmissionPageNumber: undefined,
      orderSubmissionLineNumber: undefined,
      orderSubmissionFiles: undefined,
      additionalServices: {
        realtime: false,
        roughDraft: false,
        depositionSummary: false
      }
    };
    updateNestedFormData('attorneys', attorneyIndex, 'transcriptOrders', updatedOrders);
  };

  // Handle new attorney fields
  const handleNewAttorneyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewAttorney(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle new attorney transcript order changes
  const handleNewAttorneyOrderChange = (
    orderIndex: number,
    field: keyof TranscriptOrder,
    value: TranscriptOrder[keyof TranscriptOrder]
  ) => {
    setNewAttorney(prev => {
      const updatedOrders = [...prev.transcriptOrders];
      updatedOrders[orderIndex] = {
        ...updatedOrders[orderIndex],
        [field]: value
      };
      
      return {
        ...prev,
        transcriptOrders: updatedOrders
      };
    });
  };

  // Handle new attorney additional services for a specific order
  const handleNewAttorneyServiceChange = (
    orderIndex: number,
    service: keyof AdditionalServices, 
    checked: boolean
  ) => {
    setNewAttorney(prev => {
      const updatedOrders = [...prev.transcriptOrders];
      const currentOrder = updatedOrders[orderIndex];
      
      updatedOrders[orderIndex] = {
        ...currentOrder,
        additionalServices: {
          ...(currentOrder.additionalServices || {
            realtime: false,
            roughDraft: false,
            depositionSummary: false
          }),
          [service]: checked
        }
      };
      
      return {
        ...prev,
        transcriptOrders: updatedOrders
      };
    });
  };

  // Add an attorney
  const addAttorney = () => {
    if (newAttorney.name.trim() && newAttorney.email.trim()) {
      // Use existing ID if it exists, otherwise create a new one
      // We ONLY want one consistent ID assigned ever
      const existingId = newAttorney.id;
      const id = existingId && existingId.startsWith('attorney-') 
        ? existingId  // Keep the same ID that was used during file uploads
        : `attorney-${Date.now()}-${formData.attorneys.length}`;
      
      devLog(`Adding attorney with ID: ${id}`);
      
      // Create a deep clone of transcript orders to safely modify them
      const clonedTranscriptOrders = newAttorney.transcriptOrders.map(order => {
        // Create a deep copy of the order
        const orderCopy = { ...order };
        
        // If there are order submission files, preserve them but don't update their attorney ID
        // since we're keeping the same ID throughout the process
        if (order.orderSubmissionFiles && order.orderSubmissionFiles.length > 0) {
          devLog(`Preserving ${order.orderSubmissionFiles.length} order submission files for order ${order.deponentId}`);
          // Make a deep copy of the files array to prevent reference issues
          orderCopy.orderSubmissionFiles = [...order.orderSubmissionFiles];
        }
        
        return orderCopy;
      });
      
      // Use the existing transcript orders from the newAttorney state
      // This preserves any changes made to the transcript orders in the form
      const newAttorneyData = { 
        ...newAttorney, 
        id, // Use the consistent ID
        transcriptOrders: clonedTranscriptOrders
      };
      
      addItemToArray('attorneys', newAttorneyData);
      
      // Set this attorney to be expanded
      setExpandedAttorney(id);
      
      // Also expand the first transcript order if there are any deponents
      if (clonedTranscriptOrders.length > 0) {
        setExpandedOrders({
          ...expandedOrders,
          [id]: clonedTranscriptOrders[0].deponentId
        });
      }
      
      // Reset the form with fresh transcript orders
      // Note: We're still creating fresh transcript orders for the reset form
      const freshTranscriptOrders = formData.deponents.map(deponent => ({
        deponentId: deponent.id,
        orderType: 'No Order' as const,
        additionalServices: {
          realtime: false,
          roughDraft: false,
          depositionSummary: false
        }
      }));
      
      // Generate a new consistent ID for the next attorney
      const nextAttorneyId = `attorney-${Date.now()}-${formData.attorneys.length + 1}`;
      devLog(`Resetting attorney form with new ID: ${nextAttorneyId}`);
      
      setNewAttorney({
        id: nextAttorneyId,
        name: '',
        email: '',
        firm: '',
        mailingAddress: '',
        telephonic: false,
        transcriptOrders: freshTranscriptOrders
      });
    }
  };

  // Remove an attorney
  const removeAttorney = (index: number) => {
    if (window.confirm('Are you sure you want to remove this attorney?')) {
      const attorney = formData.attorneys[index];
      
      // If removing the currently expanded attorney, expand the first remaining one
      if (expandedAttorney === attorney.id) {
        const remainingAttorneys = formData.attorneys.filter((_, i) => i !== index);
        if (remainingAttorneys.length > 0) {
          setExpandedAttorney(remainingAttorneys[0].id);
        } else {
          setExpandedAttorney('');
        }
      }
      
      removeItemFromArray('attorneys', index);
    }
  };

  // Continue to next step
  const handleNext = () => {
    // If there's a partially filled attorney form with at least name and email, add it before proceeding
    if (newAttorney.name.trim() && newAttorney.email.trim()) {
      devLog("Handling next with partially filled attorney form - adding attorney", newAttorney.name);
      
      // Add the attorney first
      addAttorney();
      
      // Small delay to ensure the attorney is added to the form state
      setTimeout(() => {
        // Then validate and proceed
        let isValid = true;
        
        // Check if all attorneys that need mailing addresses have them
        formData.attorneys.forEach((attorney) => {
          if (attorneyNeedsMailingAddress(attorney) && !attorney.mailingAddress) {
            isValid = false;
          }
        });

        // Let the form context validate and record errors
        validateStep('attorney-info');

        if (isValid && onNext) onNext();
      }, 100);
    } else {
      // No partially filled attorney, just validate and proceed
      let isValid = true;

      // Check if all attorneys that need mailing addresses have them
      formData.attorneys.forEach((attorney) => {
        if (attorneyNeedsMailingAddress(attorney) && !attorney.mailingAddress) {
          isValid = false;
        }
      });
      
      // Let the form context validate and record errors
      validateStep('attorney-info');
      
      if (isValid && onNext) onNext();
    }
  };

  // Get deponent name from ID
  const getDeponentName = (deponentId: string): string => {
    const deponent = formData.deponents.find(d => d.id === deponentId);
    return deponent ? deponent.name : 'Unknown Deponent';
  };

  // Check if an order requires mailing address
  const doesOrderNeedMailingAddress = (order: TranscriptOrder): boolean => {
    return (
      (order.orderType === 'Original' || order.orderType === 'Copy' || order.orderType === 'FREE Copy') &&
      (order.transcriptFormat === 'Physical Only' || 
       order.transcriptFormat === 'Email & Physical')
    );
  };

  // Check if any of attorney's orders need a mailing address
  const attorneyNeedsMailingAddress = (attorney: Attorney): boolean => {
    return attorney.transcriptOrders.some(order => doesOrderNeedMailingAddress(order));
  };
  
  // No longer needed - we'll determine this dynamically from actual orders

  // Helper to determine whether to show transcript format options
  const shouldShowFormatOptions = (orderType: TranscriptOrder['orderType']): boolean => {
    return orderType === 'Original' || orderType === 'Copy' || orderType === 'FREE Copy';
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h2" component="h2" gutterBottom>
          Attorney Information & Transcript Orders
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Add attorneys and their transcript orders for each deponent. Specify delivery preferences.
        </Typography>

        {/* List of existing attorneys as accordions */}
        {formData.attorneys.length > 0 ? (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" gutterBottom>
              Attorneys
            </Typography>
            
            {formData.attorneys.map((attorney, attorneyIndex) => (
              <Accordion 
                key={attorney.id} 
                expanded={expandedAttorney === attorney.id}
                onChange={handleAttorneyAccordion(attorney.id)}
                sx={{
                  mb: 2,
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: '4px',
                  '&:before': { display: 'none' }
                }}
              >
                <div style={{ position: 'relative' }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`attorney-${attorney.id}-content`}
                    id={`attorney-${attorney.id}-header`}
                    sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 6 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {attorney.name || 'Unnamed Attorney'}
                        </Typography>
                        {attorney.firm && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {attorney.firm}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  
                  <div 
                    style={{ 
                      position: 'absolute', 
                      right: '48px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      zIndex: 1 
                    }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent accordion toggle
                    }}
                  >
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation(); // Also stop propagation here
                        removeAttorney(attorneyIndex);
                      }}
                      aria-label="Remove attorney"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </div>
                </div>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Attorney Name"
                        value={attorney.name}
                        onChange={(e) => handleChange(attorneyIndex, 'name', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={attorney.email}
                        onChange={(e) => handleChange(attorneyIndex, 'email', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Firm Name"
                        value={attorney.firm}
                        onChange={(e) => handleChange(attorneyIndex, 'firm', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={attorney.telephonic || false}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(attorneyIndex, 'telephonic', e.target.checked)}
                          />
                        }
                        label="Attorney attended telephonically"
                      />
                    </Grid>
                    {attorneyNeedsMailingAddress(attorney) && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Mailing Address (required for physical transcript delivery)"
                          value={attorney.mailingAddress || ''}
                          onChange={(e) => handleChange(attorneyIndex, 'mailingAddress', e.target.value)}
                          error={!attorney.mailingAddress}
                          helperText={!attorney.mailingAddress ? 'Mailing address is required for physical delivery' : ''}
                          required={true}
                        />
                      </Grid>
                    )}
                  </Grid>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  {/* Deponent transcript orders */}
                  <Typography variant="subtitle1" gutterBottom>
                    Transcript Orders
                  </Typography>
                  
                  {formData.deponents.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No deponents available. Add deponents first to create transcript orders.
                    </Alert>
                  ) : attorney.transcriptOrders.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No transcript orders yet. Add orders for specific deponents below.
                    </Alert>
                  ) : (
                    <Box sx={{ mb: 3 }}>
                      {attorney.transcriptOrders.map((order, orderIndex) => {
                        const deponentName = getDeponentName(order.deponentId);
                        return (
                          <Accordion
                            key={`order-${orderIndex}`}
                            expanded={expandedOrders[attorney.id] === order.deponentId}
                            onChange={handleOrderAccordion(attorney.id, order.deponentId)}
                            sx={{ 
                              mb: 1,
                              border: '1px solid rgba(0, 0, 0, 0.08)',
                              '&:before': { display: 'none' }
                            }}
                          >
                            <div style={{ position: 'relative' }}>
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`order-${orderIndex}-content`}
                                id={`order-${orderIndex}-header`}
                                sx={{ backgroundColor: 'rgba(0, 0, 0, 0.01)' }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 6 }}>
                                  <Typography variant="body1">
                                    {deponentName} - {order.orderType}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              
                              {order.orderType !== 'No Order' && (
                                <div 
                                  style={{ 
                                    position: 'absolute', 
                                    right: '48px', 
                                    top: '50%', 
                                    transform: 'translateY(-50%)',
                                    zIndex: 1 
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent accordion toggle
                                  }}
                                >
                                  <IconButton
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Also stop propagation here
                                      resetTranscriptOrder(attorneyIndex, orderIndex);
                                    }}
                                    size="small"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </div>
                              )}
                            </div>
                            <AccordionDetails>
                              <Grid container spacing={3}>
                                <Grid item xs={12}>
                                  <FormControl component="fieldset">
                                    <FormLabel component="legend">Order Type</FormLabel>
                                    <RadioGroup
                                      row
                                      value={order.orderType}
                                      onChange={(e) => handleTranscriptOrderChange(
                                        attorneyIndex, 
                                        orderIndex, 
                                        'orderType', 
                                        e.target.value
                                      )}
                                    >
                                      <FormControlLabel value="Original" control={<Radio tabIndex={0} />} label="Original" />
                                      <FormControlLabel value="Copy" control={<Radio tabIndex={0} />} label="Copy" />
                                      <FormControlLabel value="FREE Copy" control={<Radio tabIndex={0} />} label="FREE Copy" />
                                      <FormControlLabel value="Copy Estimate Only" control={<Radio tabIndex={0} />} label="Copy Estimate Only" />
                                      <FormControlLabel value="Read & Sign Only" control={<Radio tabIndex={0} />} label="Read & Sign Only" />
                                      <FormControlLabel value="No Order" control={<Radio tabIndex={0} />} label="No Order" />
                                    </RadioGroup>
                                  </FormControl>
                                </Grid>
                                
                                {shouldShowFormatOptions(order.orderType) && (
                                  <>
                                    <Grid item xs={12} md={order.orderType === 'Copy' ? 6 : 12}>
                                      <FormControl fullWidth>
                                        <InputLabel id={`transcript-format-label-${orderIndex}`}>Transcript Format</InputLabel>
                                        <Select
                                          labelId={`transcript-format-label-${orderIndex}`}
                                          value={order.transcriptFormat || ''}
                                          onChange={(e) => handleTranscriptOrderChange(
                                            attorneyIndex, 
                                            orderIndex, 
                                            'transcriptFormat', 
                                            e.target.value
                                          )}
                                          label="Transcript Format"
                                        >
                                          <MenuItem value="Email Only">Email Only</MenuItem>
                                          <MenuItem value="Physical Only">Physical Only</MenuItem>
                                          <MenuItem value="Email & Physical">Email & Physical</MenuItem>
                                          <MenuItem value="Other">Other</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    
                                    {/* Order Submission Method section for Copy orders */}
                                    {order.orderType === 'Copy' && (
                                      <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                          <InputLabel id={`order-submission-method-label-${orderIndex}`}>Order Submission Method</InputLabel>
                                          <Select
                                            labelId={`order-submission-method-label-${orderIndex}`}
                                            value={order.orderSubmissionMethod || ''}
                                            onChange={(e) => handleTranscriptOrderChange(
                                              attorneyIndex,
                                              orderIndex,
                                              'orderSubmissionMethod',
                                              e.target.value
                                            )}
                                            label="Order Submission Method"
                                          >
                                            <MenuItem value="Email">Via email</MenuItem>
                                            <MenuItem value="Order form">Order form</MenuItem>
                                            <MenuItem value="Audio clip - On the Record">Audio clip - On the Record</MenuItem>
                                            <MenuItem value="On the Record">On the Record</MenuItem>
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                    )}
                                    
                                    {order.transcriptFormat === 'Other' && (
                                      <Grid item xs={12}>
                                        <TextField
                                          fullWidth
                                          label="Special Note"
                                          value={order.specialNote || ''}
                                          onChange={(e) => handleTranscriptOrderChange(
                                            attorneyIndex, 
                                            orderIndex, 
                                            'specialNote', 
                                            e.target.value
                                          )}
                                          placeholder="Include mailing address if applicable"
                                        />
                                      </Grid>
                                    )}
                                    
                                    <Grid item xs={12} md={order.orderType === 'Copy' ? 6 : 12}>
                                      <Stack spacing={2}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                          <Typography sx={{ mb: 1 }}>Delivery Timeline:</Typography>
                                          <Box sx={{ 
                                            display: 'flex', 
                                            border: '1px solid rgba(0, 0, 0, 0.12)', 
                                            borderRadius: '4px', 
                                            overflow: 'hidden' 
                                          }}>
                                            <Box 
                                              onClick={() => handleTranscriptOrderChange(
                                                attorneyIndex,
                                                orderIndex,
                                                'deliveryType',
                                                'Regular'
                                              )}
                                              sx={{ 
                                                flex: 1, 
                                                p: 1.5, 
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                bgcolor: order.deliveryType !== 'Expedited' ? 'primary.light' : 'transparent',
                                                color: order.deliveryType !== 'Expedited' ? 'white' : 'inherit',
                                                fontWeight: order.deliveryType !== 'Expedited' ? 'bold' : 'normal',
                                                '&:hover': {
                                                  bgcolor: order.deliveryType !== 'Expedited' ? 'primary.light' : 'action.hover'
                                                }
                                              }}
                                            >
                                              Regular
                                            </Box>
                                            <Box 
                                              onClick={() => handleTranscriptOrderChange(
                                                attorneyIndex,
                                                orderIndex,
                                                'deliveryType',
                                                'Expedited'
                                              )}
                                              sx={{ 
                                                flex: 1, 
                                                p: 1.5, 
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                bgcolor: order.deliveryType === 'Expedited' ? 'primary.light' : 'transparent',
                                                color: order.deliveryType === 'Expedited' ? 'white' : 'inherit',
                                                fontWeight: order.deliveryType === 'Expedited' ? 'bold' : 'normal',
                                                '&:hover': {
                                                  bgcolor: order.deliveryType === 'Expedited' ? 'primary.light' : 'action.hover'
                                                }
                                              }}
                                            >
                                              Expedited
                                            </Box>
                                          </Box>
                                        </Box>

                                        {order.deliveryType === 'Expedited' && (
                                          <>
                                            <FormControl fullWidth sx={{ mb: 2 }}>
                                              <InputLabel id={`delivery-timeline-label-${orderIndex}`}>Delivery Timeline</InputLabel>
                                              <Select
                                                labelId={`delivery-timeline-label-${orderIndex}`}
                                                value={order.deliveryTimeline || ''}
                                                onChange={(e) => handleTranscriptOrderChange(
                                                  attorneyIndex,
                                                  orderIndex,
                                                  'deliveryTimeline',
                                                  e.target.value
                                                )}
                                                label="Delivery Timeline"
                                              >
                                                <MenuItem value="Same Day">Same Day</MenuItem>
                                                <MenuItem value="Next Day">Next Day</MenuItem>
                                                <MenuItem value="2 Day">2 Day</MenuItem>
                                                <MenuItem value="3 Day">3 Day</MenuItem>
                                                <MenuItem value="4 Day">4 Day</MenuItem>
                                                <MenuItem value="5 Day">5 Day</MenuItem>
                                                <MenuItem value="6 Day">6 Day</MenuItem>
                                                <MenuItem value="7 Day">7 Day</MenuItem>
                                              </Select>
                                            </FormControl>
                                            
                                            <Box>
                                              <Typography variant="subtitle1" gutterBottom>
                                                Due Date (required):
                                              </Typography>
                                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <input
                                                  type="date"
                                                  placeholder="MM/DD/YYYY"
                                                  value={order.dueDate || ''}
                                                  onChange={(e) => handleTranscriptOrderChange(
                                                    attorneyIndex,
                                                    orderIndex,
                                                    'dueDate',
                                                    e.target.value
                                                  )}
                                                  className="MuiOutlinedInput-input"
                                                  style={{
                                                    padding: '8.5px 14px',
                                                    fontSize: '1rem',
                                                    lineHeight: '1.4375em',
                                                    border: '1px solid rgba(0, 0, 0, 0.23)',
                                                    borderRadius: '4px',
                                                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                                    width: '100%'
                                                  }}
                                                />
                                              </Box>
                                            </Box>
                                          </>
                                        )}
                                      </Stack>
                                    </Grid>
                                    
                                    {/* Order Submission Section - File upload or page/line number now beside the delivery controls */}
                                    {order.orderType === 'Copy' && order.orderSubmissionMethod && (
                                      <Grid item xs={12} md={6}>
                                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                          {(order.orderSubmissionMethod === 'Email' || order.orderSubmissionMethod === 'Order form') ? (
                                            <FileUploadField
                                              title={`Upload ${order.orderSubmissionMethod === 'Email' ? 'Email' : 'Order Form'}`}
                                              type="orderSubmissionFiles" 
                                              required={true}
                                              deponentId={order.deponentId}
                                              attorneyId={attorney.id}
                                              maxFileSize={5}
                                            />
                                          ) : (
                                            <Box>
                                              <Typography variant="subtitle2" gutterBottom>
                                                Order Details
                                              </Typography>
                                              <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                  <TextField
                                                    fullWidth
                                                    label="Page Number"
                                                    value={order.orderSubmissionPageNumber || ''}
                                                    onChange={(e) => handleTranscriptOrderChange(
                                                      attorneyIndex,
                                                      orderIndex,
                                                      'orderSubmissionPageNumber',
                                                      e.target.value
                                                    )}
                                                    required
                                                    placeholder="Enter page number"
                                                    size="small"
                                                    margin="normal"
                                                  />
                                                </Grid>
                                                <Grid item xs={12}>
                                                  <TextField
                                                    fullWidth
                                                    label="Line Number (Optional)"
                                                    value={order.orderSubmissionLineNumber || ''}
                                                    onChange={(e) => handleTranscriptOrderChange(
                                                      attorneyIndex,
                                                      orderIndex,
                                                      'orderSubmissionLineNumber',
                                                      e.target.value
                                                    )}
                                                    placeholder="Enter line number"
                                                    size="small"
                                                    margin="normal"
                                                  />
                                                </Grid>
                                              </Grid>
                                            </Box>
                                          )}
                                        </Paper>
                                      </Grid>
                                    )}
                                  </>
                                )}
                              </Grid>
                              
                              {/* Additional Services for each deponent */}
                              <Grid item xs={12}>
                                <Box sx={{ mt: 2 }}>
                                  <Divider sx={{ mb: 2 }} />
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Services for {getDeponentName(order.deponentId)}:</Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox 
                                            checked={order.additionalServices?.realtime || false}
                                            onChange={(e) => handleServiceChange(attorneyIndex, orderIndex, 'realtime', e.target.checked)}
                                          />
                                        }
                                        label="Real-time"
                                      />
                                    </Grid>
                                    <Grid item xs={4}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox 
                                            checked={order.additionalServices?.roughDraft || false}
                                            onChange={(e) => handleServiceChange(attorneyIndex, orderIndex, 'roughDraft', e.target.checked)}
                                          />
                                        }
                                        label="Rough Draft"
                                      />
                                    </Grid>
                                    <Grid item xs={4}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox 
                                            checked={order.additionalServices?.depositionSummary || false}
                                            onChange={(e) => handleServiceChange(attorneyIndex, orderIndex, 'depositionSummary', e.target.checked)}
                                          />
                                        }
                                        label="Deposition Summary"
                                      />
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  )}
                  
                  {/* We've removed the "Add Transcript Order" section since we auto-populate all deponents */}
                  
                  
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            No attorneys added yet. Use the form below to add an attorney.
          </Alert>
        )}

        {/* Form to add new attorney */}
        <Box sx={{ mb: 4 }}>
          <Accordion
            sx={{
              mb: 2,
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '4px',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="add-attorney-content"
              id="add-attorney-header"
              sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
            >
              <Typography variant="subtitle1" fontWeight="medium">
                Add New Attorney
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Attorney Name"
                    value={newAttorney.name}
                    onChange={handleNewAttorneyChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email Address"
                    type="email"
                    value={newAttorney.email}
                    onChange={handleNewAttorneyChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="firm"
                    label="Firm Name"
                    value={newAttorney.firm}
                    onChange={handleNewAttorneyChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={newAttorney.telephonic || false}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setNewAttorney(prev => ({
                            ...prev,
                            telephonic: e.target.checked
                          }));
                        }}
                      />
                    }
                    label="Attorney attended telephonically"
                  />
                </Grid>
                {attorneyNeedsMailingAddress(newAttorney) && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      name="mailingAddress"
                      label="Mailing Address (required for physical transcript delivery)"
                      value={newAttorney.mailingAddress || ''}
                      onChange={handleNewAttorneyChange}
                      error={!newAttorney.mailingAddress}
                      helperText={!newAttorney.mailingAddress ? 'Mailing address is required for physical delivery' : ''}
                      required={true}
                    />
                  </Grid>
                )}
              </Grid>
              
              {/* Preview transcript orders for new attorney */}
              {formData.deponents.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Transcript Orders
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configure transcript orders for each deponent before adding this attorney.
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    {newAttorney.transcriptOrders.map((order, orderIndex) => {
                      const deponentName = getDeponentName(order.deponentId);
                      return (
                        <Accordion
                          key={`new-order-${orderIndex}`}
                          sx={{ 
                            mb: 1,
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            '&:before': { display: 'none' }
                          }}
                        >
                          <div style={{ position: 'relative' }}>
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls={`new-order-${orderIndex}-content`}
                              id={`new-order-${orderIndex}-header`}
                              sx={{ backgroundColor: 'rgba(0, 0, 0, 0.01)' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Typography variant="body1">
                                  {deponentName} - {order.orderType}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                          </div>
                          <AccordionDetails>
                            <Grid container spacing={3}>
                              <Grid item xs={12}>
                                <FormControl component="fieldset">
                                  <FormLabel component="legend">Order Type</FormLabel>
                                  <RadioGroup
                                    row
                                    value={order.orderType}
                                    onChange={(e) => handleNewAttorneyOrderChange(
                                      orderIndex, 
                                      'orderType', 
                                      e.target.value
                                    )}
                                  >
                                    <FormControlLabel value="Original" control={<Radio tabIndex={0} />} label="Original" />
                                    <FormControlLabel value="Copy" control={<Radio tabIndex={0} />} label="Copy" />
                                    <FormControlLabel value="FREE Copy" control={<Radio tabIndex={0} />} label="FREE Copy" />
                                    <FormControlLabel value="Copy Estimate Only" control={<Radio tabIndex={0} />} label="Copy Estimate Only" />
                                    <FormControlLabel value="Read & Sign Only" control={<Radio tabIndex={0} />} label="Read & Sign Only" />
                                    <FormControlLabel value="No Order" control={<Radio tabIndex={0} />} label="No Order" />
                                  </RadioGroup>
                                </FormControl>
                              </Grid>
                              
                              {shouldShowFormatOptions(order.orderType) && (
                                <>
                                  <Grid item xs={12} md={order.orderType === 'Copy' ? 6 : 12}>
                                    <FormControl fullWidth>
                                      <InputLabel id={`new-transcript-format-label-${orderIndex}`}>Transcript Format</InputLabel>
                                      <Select
                                        labelId={`new-transcript-format-label-${orderIndex}`}
                                        value={order.transcriptFormat || ''}
                                        onChange={(e) => handleNewAttorneyOrderChange(
                                          orderIndex, 
                                          'transcriptFormat', 
                                          e.target.value
                                        )}
                                        label="Transcript Format"
                                      >
                                        <MenuItem value="Email Only">Email Only</MenuItem>
                                        <MenuItem value="Physical Only">Physical Only</MenuItem>
                                        <MenuItem value="Email & Physical">Email & Physical</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  
                                  {/* Order Submission Method section for Copy orders */}
                                  {order.orderType === 'Copy' && (
                                    <Grid item xs={12} md={4}>
                                      <FormControl fullWidth>
                                        <InputLabel id={`new-order-submission-method-label-${orderIndex}`}>Order Submission Method</InputLabel>
                                        <Select
                                          labelId={`new-order-submission-method-label-${orderIndex}`}
                                          value={order.orderSubmissionMethod || ''}
                                          onChange={(e) => handleNewAttorneyOrderChange(
                                            orderIndex,
                                            'orderSubmissionMethod',
                                            e.target.value
                                          )}
                                          label="Order Submission Method"
                                        >
                                          <MenuItem value="Email">Via email</MenuItem>
                                          <MenuItem value="Order form">Order form</MenuItem>
                                          <MenuItem value="Audio clip - On the Record">Audio clip - On the Record</MenuItem>
                                          <MenuItem value="On the Record">On the Record</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                  )}
                                  
                                  {order.transcriptFormat === 'Other' && (
                                    <Grid item xs={12}>
                                      <TextField
                                        fullWidth
                                        label="Special Note"
                                        value={order.specialNote || ''}
                                        onChange={(e) => handleNewAttorneyOrderChange(
                                          orderIndex, 
                                          'specialNote', 
                                          e.target.value
                                        )}
                                        placeholder="Include mailing address if applicable"
                                      />
                                    </Grid>
                                  )}
                                  
                                  <Grid item xs={12} md={order.orderType === 'Copy' ? 6 : 12}>
                                    <Stack spacing={2}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ mb: 1 }}>Delivery Timeline:</Typography>
                                        <Box sx={{ 
                                          display: 'flex', 
                                          border: '1px solid rgba(0, 0, 0, 0.12)', 
                                          borderRadius: '4px', 
                                          overflow: 'hidden' 
                                        }}>
                                          <Box 
                                            onClick={() => handleNewAttorneyOrderChange(
                                              orderIndex,
                                              'deliveryType',
                                              'Regular'
                                            )}
                                            sx={{ 
                                              flex: 1, 
                                              p: 1.5, 
                                              textAlign: 'center',
                                              cursor: 'pointer',
                                              bgcolor: order.deliveryType !== 'Expedited' ? 'primary.light' : 'transparent',
                                              color: order.deliveryType !== 'Expedited' ? 'white' : 'inherit',
                                              fontWeight: order.deliveryType !== 'Expedited' ? 'bold' : 'normal',
                                              '&:hover': {
                                                bgcolor: order.deliveryType !== 'Expedited' ? 'primary.light' : 'action.hover'
                                              }
                                            }}
                                          >
                                            Regular
                                          </Box>
                                          <Box 
                                            onClick={() => handleNewAttorneyOrderChange(
                                              orderIndex,
                                              'deliveryType',
                                              'Expedited'
                                            )}
                                            sx={{ 
                                              flex: 1, 
                                              p: 1.5, 
                                              textAlign: 'center',
                                              cursor: 'pointer',
                                              bgcolor: order.deliveryType === 'Expedited' ? 'primary.light' : 'transparent',
                                              color: order.deliveryType === 'Expedited' ? 'white' : 'inherit',
                                              fontWeight: order.deliveryType === 'Expedited' ? 'bold' : 'normal',
                                              '&:hover': {
                                                bgcolor: order.deliveryType === 'Expedited' ? 'primary.light' : 'action.hover'
                                              }
                                            }}
                                          >
                                            Expedited
                                          </Box>
                                        </Box>
                                      </Box>

                                      {order.deliveryType === 'Expedited' && (
                                        <>
                                          <FormControl fullWidth sx={{ mb: 2 }}>
                                            <InputLabel id={`new-delivery-timeline-label-${orderIndex}`}>Delivery Timeline</InputLabel>
                                            <Select
                                              labelId={`new-delivery-timeline-label-${orderIndex}`}
                                              value={order.deliveryTimeline || ''}
                                              onChange={(e) => handleNewAttorneyOrderChange(
                                                orderIndex,
                                                'deliveryTimeline',
                                                e.target.value
                                              )}
                                              label="Delivery Timeline"
                                            >
                                              <MenuItem value="Same Day">Same Day</MenuItem>
                                              <MenuItem value="Next Day">Next Day</MenuItem>
                                              <MenuItem value="2 Day">2 Day</MenuItem>
                                              <MenuItem value="3 Day">3 Day</MenuItem>
                                              <MenuItem value="4 Day">4 Day</MenuItem>
                                              <MenuItem value="5 Day">5 Day</MenuItem>
                                              <MenuItem value="6 Day">6 Day</MenuItem>
                                              <MenuItem value="7 Day">7 Day</MenuItem>
                                            </Select>
                                          </FormControl>
                                          
                                          <Box>
                                            <Typography variant="subtitle1" gutterBottom>
                                              Due Date (required):
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              <input
                                                type="date"
                                                placeholder="MM/DD/YYYY"
                                                value={order.dueDate || ''}
                                                onChange={(e) => handleNewAttorneyOrderChange(
                                                  orderIndex,
                                                  'dueDate',
                                                  e.target.value
                                                )}
                                                className="MuiOutlinedInput-input"
                                                style={{
                                                  padding: '8.5px 14px',
                                                  fontSize: '1rem',
                                                  lineHeight: '1.4375em',
                                                  border: '1px solid rgba(0, 0, 0, 0.23)',
                                                  borderRadius: '4px',
                                                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                                  width: '100%'
                                                }}
                                              />
                                            </Box>
                                          </Box>
                                        </>
                                      )}
                                    </Stack>
                                  </Grid>
                                  
                                  {/* Order Submission Section - File upload or page/line number now beside the delivery controls */}
                                  {order.orderType === 'Copy' && order.orderSubmissionMethod && (
                                    <Grid item xs={12} md={6}>
                                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                        {(order.orderSubmissionMethod === 'Email' || order.orderSubmissionMethod === 'Order form') ? (
                                          <FileUploadField
                                            title={`Upload ${order.orderSubmissionMethod === 'Email' ? 'Email' : 'Order Form'}`}
                                            type="orderSubmissionFiles"
                                            required={true}
                                            deponentId={order.deponentId}
                                            attorneyId={newAttorney.id} // Use the consistent ID already assigned to newAttorney
                                            maxFileSize={5}
                                          />
                                        ) : (
                                          <Box>
                                            <Typography variant="subtitle2" gutterBottom>
                                              Order was recorded on
                                            </Typography>
                                            <Grid container spacing={2}>
                                              <Grid item xs={12}>
                                                <TextField
                                                  fullWidth
                                                  label="Page Number"
                                                  value={order.orderSubmissionPageNumber || ''}
                                                  onChange={(e) => handleNewAttorneyOrderChange(
                                                    orderIndex,
                                                    'orderSubmissionPageNumber',
                                                    e.target.value
                                                  )}
                                                  required
                                                  placeholder="Enter page number"
                                                  size="small"
                                                  margin="normal"
                                                />
                                              </Grid>
                                              <Grid item xs={12}>
                                                <TextField
                                                  fullWidth
                                                  label="Line Number (Optional)" 
                                                  value={order.orderSubmissionLineNumber || ''}
                                                  onChange={(e) => handleNewAttorneyOrderChange(
                                                    orderIndex,
                                                    'orderSubmissionLineNumber',
                                                    e.target.value
                                                  )}
                                                  placeholder="Enter line number"
                                                  size="small"
                                                  margin="normal"
                                                />
                                              </Grid>
                                            </Grid>
                                          </Box>
                                        )}
                                      </Paper>
                                    </Grid>
                                  )}
                                </>
                              )}
                            </Grid>
                            
                            {/* Additional Services for each deponent in new attorney form */}
                            <Grid item xs={12}>
                              <Box sx={{ mt: 2 }}>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Services for {getDeponentName(order.deponentId)}:</Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={4}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox 
                                          checked={order.additionalServices?.realtime || false}
                                          onChange={(e) => handleNewAttorneyServiceChange(orderIndex, 'realtime', e.target.checked)}
                                        />
                                      }
                                      label="Real-time"
                                    />
                                  </Grid>
                                  <Grid item xs={4}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox 
                                          checked={order.additionalServices?.roughDraft || false}
                                          onChange={(e) => handleNewAttorneyServiceChange(orderIndex, 'roughDraft', e.target.checked)}
                                        />
                                      }
                                      label="Rough Draft"
                                    />
                                  </Grid>
                                  <Grid item xs={4}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox 
                                          checked={order.additionalServices?.depositionSummary || false}
                                          onChange={(e) => handleNewAttorneyServiceChange(orderIndex, 'depositionSummary', e.target.checked)}
                                        />
                                      }
                                      label="Deposition Summary"
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                </>
              )}
              
              
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={addAttorney}
                  disabled={!newAttorney.name.trim() || !newAttorney.email.trim()}
                >
                  Add Attorney
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
          >
            Continue to Payment
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AttorneyInfo;